import { useEffect, useState } from 'react'
import { Outlet, NavLink, useLocation, useNavigate, useParams } from 'react-router-dom'
import { useAuthStore } from '../../stores/auth.js'
import { useTreeStore } from '../../stores/tree.js'
import { api } from '../../services/api.js'
import { isNativeApp } from '../../utils/platform.js'
import MobileTabBar from './MobileTabBar.jsx'

const TIER_LABELS = { free: 'Free', plus: 'Plus', researcher: 'Researcher', family: 'Researcher', archive: 'Researcher' }

/* ── SVG icons ──────────────────────────────────────────────────────────── */
const Icons = {
  trees: (
    <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.5">
      <circle cx="12" cy="4" r="2"/><circle cx="5" cy="16" r="2"/><circle cx="19" cy="16" r="2"/>
      <line x1="12" y1="6" x2="7" y2="14"/><line x1="12" y1="6" x2="17" y2="14"/>
      <line x1="12" y1="6" x2="12" y2="18"/>
    </svg>
  ),
  discover: (
    <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.5">
      <circle cx="11" cy="11" r="8"/><line x1="21" y1="21" x2="16.65" y2="16.65"/>
      <line x1="11" y1="8" x2="11" y2="14"/><line x1="8" y1="11" x2="14" y2="11"/>
    </svg>
  ),
  library: (
    <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.5">
      <path d="M4 19.5A2.5 2.5 0 016.5 17H20"/><path d="M6.5 2H20v20H6.5A2.5 2.5 0 014 19.5v-15A2.5 2.5 0 016.5 2z"/>
    </svg>
  ),
  profile: (
    <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.5">
      <path d="M20 21v-2a4 4 0 00-4-4H8a4 4 0 00-4 4v2"/><circle cx="12" cy="7" r="4"/>
    </svg>
  ),
  admin: (
    <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.5">
      <circle cx="12" cy="12" r="3"/>
      <path d="M19.4 15a1.65 1.65 0 00.33 1.82l.06.06a2 2 0 010 2.83 2 2 0 01-2.83 0l-.06-.06a1.65 1.65 0 00-1.82-.33 1.65 1.65 0 00-1 1.51V21a2 2 0 01-4 0v-.09A1.65 1.65 0 009 19.4a1.65 1.65 0 00-1.82.33l-.06.06a2 2 0 01-2.83-2.83l.06-.06A1.65 1.65 0 004.68 15a1.65 1.65 0 00-1.51-1H3a2 2 0 010-4h.09A1.65 1.65 0 004.6 9a1.65 1.65 0 00-.33-1.82l-.06-.06a2 2 0 012.83-2.83l.06.06A1.65 1.65 0 009 4.68a1.65 1.65 0 001-1.51V3a2 2 0 014 0v.09a1.65 1.65 0 001 1.51 1.65 1.65 0 001.82-.33l.06-.06a2 2 0 012.83 2.83l-.06.06A1.65 1.65 0 0019.4 9a1.65 1.65 0 001.51 1H21a2 2 0 010 4h-.09a1.65 1.65 0 00-1.51 1z"/>
    </svg>
  ),
  help: (
    <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.5">
      <circle cx="12" cy="12" r="10"/>
      <path d="M9.09 9a3 3 0 015.83 1c0 2-3 3-3 3"/>
      <line x1="12" y1="17" x2="12.01" y2="17" strokeWidth="2" strokeLinecap="round"/>
    </svg>
  ),
  chevronRight: (
    <svg width="10" height="10" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
      <polyline points="9 18 15 12 9 6"/>
    </svg>
  ),
  changelog: (
    <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.5">
      <path d="M12 3l1.9 4.3 4.6.5-3.4 3.1.9 4.6L12 13.7 8 15.6l.9-4.6L5.5 7.8l4.6-.5z"/>
    </svg>
  ),
}

function getRole(token) {
  try { return JSON.parse(atob(token.split('.')[1])).role } catch { return 'user' }
}

export default function AppShell() {
  const { user, token, logout, refresh } = useAuthStore()
  const { trees, focalPersonData } = useTreeStore()
  const navigate = useNavigate()
  const isAdmin = token && getRole(token) === 'admin'
  const [collapsed, setCollapsed] = useState(() => localStorage.getItem('sidebar_collapsed') === 'true')

  // Refetch the user on mount AND whenever the tab regains focus so tier
  // changes made server-side (admin bumps, Stripe webhook activations)
  // replace the stale localStorage copy without needing a sign-out. Focus
  // covers the "I bumped my tier in admin, switched tabs back" flow.
  useEffect(() => {
    refresh()
    const onFocus = () => refresh()
    window.addEventListener('focus', onFocus)
    return () => window.removeEventListener('focus', onFocus)
  }, [refresh])

  // Journey badge — open-task count on the sidebar. Refreshed on mount
  // and whenever the URL changes (so dismissing a task on the Journey
  // page updates the pill without a reload).
  const location = useLocation()
  const [journeyCount, setJourneyCount] = useState(0)
  const [inboxCount, setInboxCount] = useState(0)
  useEffect(() => {
    if (!token) return
    api.get('/archive/journey/count')
      .then(r => setJourneyCount(r.count || 0))
      .catch(() => {})
    // Suggestions inbox — pending reviews across trees AND canonical
    // persons the caller originated. Refreshes on route change so
    // approving from /suggestions clears the badge immediately.
    api.get('/suggestions/inbox/count')
      .then(r => setInboxCount(r.count || 0))
      .catch(() => {})
  }, [token, location.pathname])
  const [mobileOpen, setMobileOpen] = useState(false)

  function toggleSidebar() {
    setCollapsed(prev => {
      localStorage.setItem('sidebar_collapsed', String(!prev))
      return !prev
    })
  }

  function closeMobile() { setMobileOpen(false) }

  function handleLogout() {
    logout()
    navigate('/login')
  }

  // Show up to 3 trees in the sidebar for quick access
  const recentTrees = trees.slice(0, 3)

  // Native app gets a bottom tab bar (companion-first); web keeps the sidebar.
  const native = isNativeApp()

  // Flag the document so global native-feel rules (no overscroll bounce) apply.
  useEffect(() => {
    document.documentElement.classList.toggle('is-native', native)
  }, [native])

  return (
    <div className={`app-shell ${collapsed ? 'sidebar-collapsed' : ''} ${mobileOpen ? 'mobile-menu-open' : ''} ${native ? 'app-shell--native' : ''}`}>
      {/* Mobile backdrop */}
      {mobileOpen && <div className="sidebar__mobile-backdrop" onClick={closeMobile} />}

      <nav className="sidebar">
        <div className="sidebar__top">
          <div className="sidebar__logo">{collapsed ? 'G' : 'Genealogic'}</div>
          <button className="sidebar__collapse-btn sidebar__collapse-btn--desktop" onClick={toggleSidebar} title={collapsed ? 'Expand' : 'Collapse'}>
            {collapsed ? '›' : '‹'}
          </button>
          <button className="sidebar__collapse-btn sidebar__collapse-btn--mobile" onClick={closeMobile} title="Close menu">
            ✕
          </button>
        </div>

        <div className="sidebar__nav">
          {/* Primary navigation */}
          <NavLink to="/" end className={({ isActive }) => `nav-item ${isActive ? 'active' : ''}`} title="My Trees" onClick={closeMobile}>
            {Icons.trees}
            {!collapsed && <span>My Trees</span>}
          </NavLink>

          <NavLink to="/profile" className={({ isActive }) => `nav-item ${isActive ? 'active' : ''}`} title="Profile" onClick={closeMobile}>
            {Icons.profile}
            {!collapsed && <span>Profile</span>}
          </NavLink>

          <NavLink to="/billing" className={({ isActive }) => `nav-item ${isActive ? 'active' : ''}`} title="Billing & plans" onClick={closeMobile}>
            <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.5">
              <rect x="2" y="5" width="20" height="14" rx="2"/>
              <line x1="2" y1="10" x2="22" y2="10"/>
            </svg>
            {!collapsed && <span>Billing</span>}
          </NavLink>

          {/* Research / archive / my journey */}
          {!collapsed && <div className="nav-section-label">Research</div>}
          <NavLink
            to="/journey"
            className={({ isActive }) => `nav-item ${isActive ? 'active' : ''}`}
            title="My Journey — the open quests in your tree"
            onClick={closeMobile}
          >
            <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.5">
              <polyline points="9 11 12 14 22 4"/>
              <path d="M21 12v7a2 2 0 01-2 2H5a2 2 0 01-2-2V5a2 2 0 012-2h11"/>
            </svg>
            {!collapsed && <span>My Journey</span>}
            {journeyCount > 0 && !collapsed && (
              <span className="nav-pill nav-pill--count" title={`${journeyCount} open steps`}>
                {journeyCount}
              </span>
            )}
            {journeyCount > 0 && collapsed && (
              <span className="nav-badge-dot" title={`${journeyCount} open steps`}>{journeyCount > 9 ? '9+' : journeyCount}</span>
            )}
          </NavLink>
          <NavLink
            to="/archive"
            className={({ isActive }) => `nav-item ${isActive ? 'active' : ''}`}
            title="The Archive — the shared archival world"
            onClick={closeMobile}
          >
            {Icons.discover}
            {!collapsed && <span>Archive</span>}
          </NavLink>
          <NavLink
            to="/world-map"
            className={({ isActive }) => `nav-item ${isActive ? 'active' : ''}`}
            title="World Map — geographic coverage of the knowledge graph"
            onClick={closeMobile}
          >
            <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.5">
              <circle cx="12" cy="12" r="10"/>
              <path d="M2 12h20"/>
              <path d="M12 2a15 15 0 010 20"/>
              <path d="M12 2a15 15 0 000 20"/>
            </svg>
            {!collapsed && <span>World Map</span>}
          </NavLink>
          <NavLink
            to="/suggestions"
            className={({ isActive }) => `nav-item ${isActive ? 'active' : ''}`}
            title="Suggestions — pending changes on your persons and trees"
            onClick={closeMobile}
          >
            <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.5">
              <path d="M21 15a2 2 0 0 1-2 2H7l-4 4V5a2 2 0 0 1 2-2h14a2 2 0 0 1 2 2z"/>
            </svg>
            {!collapsed && <span>Suggestions</span>}
            {inboxCount > 0 && !collapsed && (
              <span className="nav-pill nav-pill--count" title={`${inboxCount} pending`}>
                {inboxCount}
              </span>
            )}
            {inboxCount > 0 && collapsed && (
              <span className="nav-badge-dot" title={`${inboxCount} pending`}>{inboxCount > 9 ? '9+' : inboxCount}</span>
            )}
          </NavLink>

          <NavLink
            to="/mysteries"
            className={({ isActive }) => `nav-item ${isActive ? 'active' : ''}`}
            title="Mysteries — research leads shared by other family historians"
            onClick={closeMobile}
          >
            <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.5">
              <circle cx="12" cy="12" r="9"/>
              <path d="M9.5 9a2.5 2.5 0 1 1 5 0c0 1.5-1.5 2-2.5 3"/>
              <circle cx="12" cy="17" r="0.6" fill="currentColor"/>
            </svg>
            {!collapsed && <span>Mysteries</span>}
          </NavLink>

          {/* Tree quick links */}
          {!collapsed && recentTrees.length > 0 && (
            <>
              <div className="nav-section-label">Your trees</div>
              {recentTrees.map(tree => (
                <button
                  key={tree.id}
                  className="nav-item nav-item--tree"
                  onClick={() => navigate(`/tree/${tree.id}`)}
                  title={tree.name}
                >
                  <span className="nav-tree-dot" />
                  <span className="nav-tree-name">{tree.name}</span>
                  {Icons.chevronRight}
                </button>
              ))}
            </>
          )}

          {isAdmin && (
            <>
              {!collapsed && <div className="nav-section-label">System</div>}
              <NavLink to="/admin" className={({ isActive }) => `nav-item ${isActive ? 'active' : ''}`} title="Admin">
                {Icons.admin}
                {!collapsed && <span>Admin</span>}
              </NavLink>
            </>
          )}

          {/* Help */}
          {!collapsed && <div className="nav-section-label">Support</div>}
          <NavLink to="/help" className={({ isActive }) => `nav-item ${isActive ? 'active' : ''}`} title="How it works">
            {Icons.help}
            {!collapsed && <span>How it works</span>}
          </NavLink>
          <NavLink to="/changelog" className={({ isActive }) => `nav-item ${isActive ? 'active' : ''}`} title="What's new">
            {Icons.changelog}
            {!collapsed && <span>What's new</span>}
          </NavLink>
        </div>

        <div className="sidebar__divider" />

        {/* Focal person — "last exploring" indicator */}
        {!collapsed && focalPersonData && (
          <div
            className="sidebar__focal"
            onClick={() => focalPersonData.treeId && navigate(`/tree/${focalPersonData.treeId}/person/${focalPersonData.id}`)}
            title="Continue exploring"
          >
            <div className="sidebar__focal-label">Last explored</div>
            <div className="sidebar__focal-name">
              {focalPersonData.preferred_name || focalPersonData.first_name || ''}
              {' '}
              <span style={{ opacity: 0.5 }}>{focalPersonData.last_name || ''}</span>
            </div>
            {focalPersonData.treeName && (
              <div className="sidebar__focal-tree">{focalPersonData.treeName}</div>
            )}
          </div>
        )}

        <div className="sidebar__footer">
          <NavLink to="/profile" className={({ isActive }) => `sidebar__user ${isActive ? 'active' : ''} ${collapsed ? 'sidebar__user--collapsed' : ''}`} title={user?.name}>
            <div className="sidebar__avatar">{user?.name?.[0]?.toUpperCase() || '?'}</div>
            {!collapsed && (
              <div>
                <div className="sidebar__user-name">{user?.name}</div>
                <div className="sidebar__user-tier">{TIER_LABELS[user?.tier] || 'Free'}</div>
              </div>
            )}
          </NavLink>
          {!collapsed && (
            <button className="sidebar__signout" onClick={handleLogout}>Sign out</button>
          )}
        </div>
      </nav>

      <main className="main-content">
        <button className="sidebar__hamburger" onClick={() => setMobileOpen(true)} aria-label="Open menu">
          <span /><span /><span />
        </button>
        <Outlet />
      </main>

      {native && <MobileTabBar inboxCount={inboxCount} />}
    </div>
  )
}
